import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ProjectCard from './projects/ProjectCard';
import ProjectFormModal from './projects/ProjectFormModal';
import ShareProjectModal from './projects/ShareProjectModal';
import ModalDialog from './common/ModalDialog';
import { projectService } from '../services/projectService';
import { useProjects } from '../context/ProjectsContext';
import { useRefreshTicketCounts } from '../hooks/useRefreshTicketCounts';

function makeListItem(project) {
  return {
    id: project.id,
    name: project.name,
    key: project.jira_project_key,
    jira_account_id: project.jira_account_id,
    jira_project_key: project.jira_project_key,
    jira_project_name: project.jira_project_name,
    start_date: project.start_date,
    end_date: project.end_date,
    dev_capacity_hours_per_month: project.dev_capacity_hours_per_month,
    ticketCount: project.ticket_count ?? 0,
    week_start_day: project.week_start_day ?? 1,
    is_owner: project.is_owner ?? true,
    is_shared: project.is_shared ?? false,
    owner_id: project.owner_id ?? null,
  };
}

export default function ProjectsView() {
  const { projects, loading, fetchProjects, setProjects } = useProjects();
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectToShare, setProjectToShare] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [editingProject, setEditingProject] = useState(false);

  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects().catch((err) => toast.error(err.message || 'Failed to load projects'));
    }
  }, [fetchProjects, projects.length]);

  useRefreshTicketCounts(projects, setProjects);
  const handleEdit = (project) => setProjectToEdit(project);
  const handleDelete = (project) => setProjectToDelete(project);
  const handleShare = (project) => setProjectToShare(project);

  const confirmDelete = async () => {
    if (!projectToDelete || deletingProject) return;
    try {
      setDeletingProject(true);
      await projectService.deleteProject(projectToDelete.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      toast.success(`Project "${projectToDelete.name}" deleted`);
      setProjectToDelete(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete project');
    } finally {
      setDeletingProject(false);
    }
  };

  const handleCreateSubmit = async (projectData) => {
    setCreatingProject(true);
    try {
      const project = await projectService.createProject(projectData);
      setProjects((prev) => [...prev, makeListItem(project)]);
      toast.success('Project created successfully');
      setShowNewProjectModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleEditSubmit = async (projectData) => {
    setEditingProject(true);
    try {
      const updated = await projectService.updateProject(projectToEdit.id, projectData);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectToEdit.id
            ? {
                ...p,
                name: updated.name,
                key: updated.jira_project_key,
                start_date: updated.start_date,
                end_date: updated.end_date,
                dev_capacity_hours_per_month: updated.dev_capacity_hours_per_month,
                week_start_day: updated.week_start_day ?? 1,
                is_owner: updated.is_owner ?? p.is_owner,
                is_shared: updated.is_shared ?? p.is_shared,
              }
            : p
        )
      );
      toast.success('Project updated successfully');
      setProjectToEdit(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update project');
    } finally {
      setEditingProject(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Projects</h1>
        <button
          type="button"
          onClick={() => setShowNewProjectModal(true)}
          className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 transition-colors"
        >
          New Project
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400">
            No projects yet. Create your first project to get started.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onShare={handleShare}
            />
          ))}
        </div>
      )}

      {projectToEdit && (
        <ProjectFormModal
          onClose={() => setProjectToEdit(null)}
          onSubmit={handleEditSubmit}
          loading={editingProject}
          editProject={projectToEdit}
        />
      )}

      {showNewProjectModal && (
        <ProjectFormModal
          onClose={() => setShowNewProjectModal(false)}
          onSubmit={handleCreateSubmit}
          loading={creatingProject}
          existingProjectKeys={projects.map((p) => p.key).filter(Boolean)}
        />
      )}

      {projectToShare && (
        <ShareProjectModal
          project={projectToShare}
          onClose={() => setProjectToShare(null)}
        />
      )}

      <ModalDialog
        open={!!projectToDelete}
        title="Delete project"
        description={
          projectToDelete ? (
            <>
              Are you sure you want to delete <strong>{projectToDelete.name}</strong>? This action cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletingProject}
        onConfirm={confirmDelete}
        onClose={() => setProjectToDelete(null)}
      />
    </div>
  );
}